<script lang="ts" setup>
import { computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import useSettingStore from '../../stores/setting';
import useUiStore from '../../stores/ui';
import useReaderStore from '../../stores/reader';
import { useDisplay } from 'vuetify';
import useRenditionStore from '../../stores/rendition';
import SideDrawer from '../SideDrawer.vue';
import SettingsCard from '../../components/settings/SettingsCard.vue';
import useLocale from '../../composables/useLocale';

type Emits = {
  (e: 'update:model-value', value: boolean): void;
  (e: 'reset'): void;
  (e: 'close'): void;
};
const emit = defineEmits<Emits>();

type Props = {
  showTooltips?: boolean;
  activator?: any;
};
withDefaults(defineProps<Props>(), {
  showTooltips: true,
});

const { smAndDown } = useDisplay();
const { audioTrackingAvailable, isAutoPageTurnAvailable, isNarrationSpeedChangeAvailable } =
  storeToRefs(useSettingStore());
const uiStore = useUiStore();
const { isSettingsVisible, alwaysShowSettings, isControlsVisible, allMenusClosed } = storeToRefs(uiStore);
const { isContinuous, isAudioOnly } = storeToRefs(useReaderStore());
const { isReflowable } = storeToRefs(useRenditionStore());
const { t } = useLocale();

const hasSettings = computed(
  () =>
    isAutoPageTurnAvailable.value ||
    audioTrackingAvailable.value ||
    isNarrationSpeedChangeAvailable.value ||
    isContinuous.value ||
    isReflowable.value ||
    alwaysShowSettings.value,
);

watch(isSettingsVisible, (value) => {
  emit('update:model-value', value);
  if (!value) {
    if (isControlsVisible.value && allMenusClosed.value) {
      uiStore.hideControlsAfterTimeout();
    }
  }
});

defineExpose({
  isControlsVisible,
  hasSettings,
});
</script>

<template>
  <v-menu
    v-if="!smAndDown && hasSettings && !isAudioOnly"
    id="settings"
    v-model="isSettingsVisible"
    :activator="activator"
    :close-on-content-click="false"
    location="top"
    close-delay="500"
    :key="`menu-${isSettingsVisible}`"
  >
    <SettingsCard @reset="emit('reset')" @close="emit('close')">
      <slot />
    </SettingsCard>
  </v-menu>
  <div v-else-if="!smAndDown && hasSettings && isAudioOnly" class="d-flex mt-1 bg-transparent">
    <SideDrawer v-model="isSettingsVisible" location="right" :title="t('label_settings')" icon="fa-regular fa-gear">
      <slot />
    </SideDrawer>
  </div>
  <v-bottom-sheet
    v-else-if="hasSettings && smAndDown"
    id="settings"
    v-model="isSettingsVisible"
    :activator="activator"
    :close-on-content-click="false"
    close-delay="300"
    :key="`sheet-${isSettingsVisible}`"
  >
    <SettingsCard @reset="emit('reset')" @close="emit('close')">
      <slot />
    </SettingsCard>
  </v-bottom-sheet>
</template>

<style lang="scss" scoped>
:deep(.v-switch__track) {
  background-color: rgb(var(--v-theme-secondary));
}

:deep(.v-icon) {
  opacity: 1 !important;
}
</style>
